import { Checkbox } from "@/design-system/components/Checkbox/Checkbox";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";

import { accessLevelLabel, type PermissionDomainGroup } from "./permissions";

interface PermissionsGridProps {
  permissionGroups: PermissionDomainGroup[];
  loading: boolean;
  // Selected permissions as canonical "<domain>:<level>" strings.
  value: Set<string>;
  onToggle: (permission: string) => void;
}

// Grouped grid of per-domain, per-access-level permission checkboxes. Shared by
// the API-token creation dialog and the internal permission-simulation modal so
// both present the exact same read/write grid over the same catalog.
export function PermissionsGrid({
  permissionGroups,
  loading,
  value,
  onToggle,
}: PermissionsGridProps) {
  if (loading) {
    return <LoadingSpinner className="size-5" />;
  }

  return (
    <div className="border-borderDefault divide-dividerDefault divide-y rounded-lg border">
      {permissionGroups.map((group) => (
        <div
          key={group.domain}
          className="flex items-center justify-between gap-4 px-4 py-3"
        >
          <span className="text-textDefault text-sm">{group.label}</span>
          <div className="flex shrink-0 gap-4">
            {group.levels.map((level) => {
              const permission = `${group.domain}:${level}`;
              return (
                <label
                  key={permission}
                  className="flex cursor-pointer items-center gap-2 text-sm"
                >
                  <Checkbox
                    className="size-5"
                    checked={value.has(permission)}
                    onCheckedChange={() => onToggle(permission)}
                  />
                  {accessLevelLabel(level)}
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
